/** @jsxImportSource @emotion/react */
import { usePrioritisationContext } from '@app/contexts/ModuleContexts/PrioritisationContext'
import { usePrioritisationMap } from '@app/contexts/ModuleMapContexts/PrioritisationMapContext'
import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  Slide,
  useTheme,
} from '@mui/material'
import { FancySuspense } from '@app/components/Atoms/FancySuspense'
import { Icon } from '@app/components/Atoms/Icon'
import { Tooltip } from '@app/components/Atoms/Tooltip/Tooltip'
import { clientNameAtom, selectedClientAtom } from '@app/state/clientsAtom'
import { useAtomValue } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { ExposureHistogram } from './ExposureHistogram'
import { PriorityTable } from './PriorityTable'
import {
  footerButtonsContainer,
  resultsScrollContainer,
  resultsTabContainer,
  resultsTabParent,
  tooltipArrow,
  viewAreasButton,
} from './ResultsTab.styles'
import {
  blobToDataUrl,
  preparePageDuplicateMapAndExportImageBlob,
} from '@app/components/Pages/Map/MapTools/ExportMap/mapExporter'
import * as turf from '@turf/turf'
import apiClient from '@app/config/axios.config'
import { canExportAtom, userAtom } from '@app/hooks/useUserProfile'
import { pollForReport } from '@app/utils/pollForReport'
import { toast } from '@app/components/ui/Toast/toast'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'

export const ResultsTab = () => {
  const theme = useTheme()
  const clientDisplayName = useAtomValue(selectedClientAtom)?.display_name as string
  const {
    scenarioGroup,
    adaptationAreaPriorities,
    area,
    setArea,
    exposurePercByElement,
    valueWeightings,
    exposurePercIsLoading,
    adaptationAreaPrioritiesIsLoading,
    elementGroups,
  } = usePrioritisationContext()
  const { selectedPrioLayer, map, flyTo, clientLocation } = usePrioritisationMap()
  const canExport = useAtomValue(canExportAtom)
  const { data: user } = useAtomValue(userAtom)

  const arrowRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [hazardGroupName, setHazardGroupName] = useState<string | null>(null)

  const clientName = useAtomValue(clientNameAtom)

  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handlePdfReportClick = async () => {
    setOpen(false)
    if (!map || !selectedPrioLayer || !hazardGroupName) return

    toast({
      description:
        'Generating data for report. This may take a while, we will notify you when the report is ready.',
      type: 'info',
    })
    let mapImageDataUrl = ''
    const mapWidth = 531.28
    const mapHeight = 308
    await preparePageDuplicateMapAndExportImageBlob(
      map,
      mapWidth,
      mapHeight,
      async (imageBlob: Blob) => {
        mapImageDataUrl = await blobToDataUrl(imageBlob)
      },
      [],
      null,
      theme,
    )
    const mapHeightInPixels = map.getCanvas().height
    const mapWidthInPixels = map.getCanvas().width
    const mapWidthInMetres = turf.distance(
      map.unproject([0, mapHeightInPixels / 2.0]).toArray(),
      map.unproject([mapWidthInPixels, mapHeightInPixels / 2.0]).toArray(),
      { units: 'meters' },
    )

    const mapImageUploadResponse = await apiClient.post(`/report/mapimage`, {
      dataUrl: mapImageDataUrl,
    })

    if (mapImageUploadResponse.status != 200) throw new Error('Map image failed to upload')

    const mapImageDownloadUrl = mapImageUploadResponse.data as string

    const report_id = `${user?.user_uuid}-${Date.now()}`
    try {
      logDataDogAction(DATADOG_ACTIONS.EXPORT_REPORT, {
        type: 'prioritisation',
        format: 'pdf',
      })

      await apiClient.post(`/report/export/${clientName}/prioritisation-report/${report_id}`, {
        mapImage: {
          bearing: map.getBearing(),
          pitch: map.getPitch(),
          mapWidthInMetres,
          url: mapImageDownloadUrl,
        },
        area,
        valueWeightings,
        scenarioGroup,
        adaptationAreaPriorities,
        areasType: selectedPrioLayer.layer_type,
        exposurePercByElement: exposurePercByElement[hazardGroupName],
        elementGroups,
        clientDisplayName,
        hazardGroupName,
      })

      const date = new Date()
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      pollForReport(report_id, `Prioritisation_Report_${year}-${month}-${day}.pdf`)
    } catch (error) {
      toast({
        description:
          'There was an error generating the report. Please notify your Urban Intelligence contact or submit feedback.',
        type: 'error',
      })
    }
  }

  const viewAllAreas = () => {
    setArea('')
    flyTo({ ...clientLocation, usePadding: true, pitch: 0, bearing: 0 })
  }

  const numberOfElements = hazardGroupName
    ? Object.keys(exposurePercByElement[hazardGroupName]).length
    : 0
  useEffect(() => {
    if (!hazardGroupName && Object.keys(exposurePercByElement).length > 0) {
      setHazardGroupName(Object.keys(exposurePercByElement)[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exposurePercByElement])

  return (
    <Box css={resultsTabParent()}>
      <Box css={resultsScrollContainer()}>
        <Box css={resultsTabContainer(theme)}>
          <h1>Results</h1>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '4px',
            }}
          >
            <Box sx={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
              <p>
                For {scenarioGroup?.scenario_id} scenario
                {area ? ` for ${area}` : ''}.{' '}
              </p>
              <Tooltip
                title={`The results show the priority ranking of each adaptation area and the ${
                  numberOfElements < 10 ? 'most' : 'top 10'
                } exposed elements ${area ? 'in ' + area : 'across the entire area'}.`}
                arrow
              >
                <Box>
                  <Icon iconName="Info" colour={theme.palette.action.active} />
                </Box>
              </Tooltip>
            </Box>
            {area && (
              <Button css={viewAreasButton} variant="text" onClick={viewAllAreas}>
                View all areas
              </Button>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <PriorityTable />
          </Box>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <h2>
              {numberOfElements < 10 ? 'The most' : 'Top 10'} exposed elements{' '}
              {area ? 'in ' + area : 'across the entire area'}
            </h2>

            <FancySuspense
              isLoading={!(exposurePercByElement && Object.keys(exposurePercByElement).length > 0)}
              variant="box"
            >
              {
                // Show if there is more than one hazard group (+ "All Hazards")
                Object.keys(exposurePercByElement).length > 2 && (
                  <Select
                    css={{ width: '224px' }}
                    value={hazardGroupName}
                    onChange={(e) => {
                      setHazardGroupName(e.target.value as string)
                    }}
                  >
                    {Object.keys(exposurePercByElement).map((hazardGroup) => (
                      <MenuItem key={hazardGroup} value={hazardGroup}>
                        {hazardGroup}
                      </MenuItem>
                    ))}
                  </Select>
                )
              }
            </FancySuspense>
            <FancySuspense isLoading={!hazardGroupName} variant="horizontalBarChartInverse">
              {hazardGroupName && <ExposureHistogram hazardGroupName={hazardGroupName} />}
            </FancySuspense>
          </Box>
        </Box>
        {canExport && (
          <Slide
            direction="up"
            in={
              !!(selectedPrioLayer && !exposurePercIsLoading && !adaptationAreaPrioritiesIsLoading)
            }
            mountOnEnter
            unmountOnExit
          >
            <Box css={footerButtonsContainer(theme, true)}>
              <Button
                ref={anchorRef}
                style={{ backgroundColor: theme.palette.secondary.main, color: '#FFF' }}
                onClick={handleToggle}
              >
                Export <Icon iconName="ChevronRight" colour="#FFF" />
              </Button>
              <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
                placement="right-end"
                modifiers={[
                  {
                    name: 'flip',
                    enabled: false,
                    options: {
                      altBoundary: true,
                      rootBoundary: 'document',
                      padding: 8,
                    },
                  },
                  {
                    name: 'preventOverflow',
                    enabled: false,
                    options: {
                      altAxis: true,
                      altBoundary: true,
                      tether: true,
                      rootBoundary: 'document',
                      padding: 8,
                    },
                  },
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 16],
                    },
                  },
                ]}
              >
                {({ TransitionProps }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: 'left bottom',
                    }}
                  >
                    <Paper
                      css={{
                        border: '2px solid',
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                      }}
                    >
                      <span
                        ref={arrowRef}
                        css={tooltipArrow(theme.palette.primary.main)}
                        className="arrow"
                      />
                      <ClickAwayListener onClickAway={handleClose}>
                        <MenuList autoFocusItem={open} id="menu-list-grow">
                          <MenuItem onClick={handlePdfReportClick}>Report (.pdf)</MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
          </Slide>
        )}
      </Box>
    </Box>
  )
}
